'use client';

import {
  useMemo,
  useState,
  useContext,
  useCallback,
  createContext,
} from 'react';

import { checkUpgradeNeeded } from 'src/utils/plan/plan-access';

import { CheckPlanUpgrade } from 'src/sections/common/plan-upgrade-popover';

/**
 * `showUpgrade`가 실제로 요구하는 모양 — **`result` 하나뿐**이다(구현이 그것만 읽고
 * 나머지는 팝오버로 그대로 넘긴다). `UpgradeCheckResult`(=`checkUpgradeNeeded` 반환)는
 * 모든 필드가 필수라, 그 타입을 그대로 파라미터에 쓰면 **직접 조립해 넘기는 호출부**
 * (검색 화면의 채널·행수 업그레이드 유도)가 TS에서 막힌다.
 */
export type UpgradeCheckResultLike = {
  result: boolean;
  isExpired?: boolean;
  currentPlan?: string;
  required?: any;
  request?: any;
  key?: string | null;
  [key: string]: any;
};

export type PlanUpgradeOptions = {
  event?: string | null;
  description?: string | null;
  onClose?: (() => void) | null;
};

export interface PlanUpgradeContextValue {
  showUpgrade: (checkPlanResult: UpgradeCheckResultLike | null, options?: PlanUpgradeOptions) => boolean;
  clearUpgrade: () => void;
  checkAndShow: (serviceKey: string, requestValue?: any, options?: PlanUpgradeOptions) => boolean;
}

const PlanUpgradeContext = createContext<PlanUpgradeContextValue | null>(null);

export type PlanUpgradeProviderProps = {
  user: any;
  children: React.ReactNode;
};

/**
 * 서비스 영역 공통 업그레이드 팝업 Provider (단일 인스턴스)
 */
export function PlanUpgradeProvider({ user, children }: PlanUpgradeProviderProps) {
  const [upgradeState, setUpgradeState] = useState<{
    checkPlanResult: any;
    options: {
      event: string | null;
      description: string | null;
      onClose: (() => void) | null;
    };
  } | null>(null);

  const clearUpgrade = useCallback(() => {
    setUpgradeState(null);
  }, []);

  const showUpgrade = useCallback((checkPlanResult: UpgradeCheckResultLike | null, options: PlanUpgradeOptions = {}) => {
    if (!checkPlanResult?.result) {
      return false;
    }

    setUpgradeState({
      checkPlanResult,
      options: {
        event: options.event ?? 'custom',
        description: options.description ?? null,
        onClose: options.onClose ?? null,
      },
    });

    return true;
  }, []);

  const checkAndShow = useCallback((
    serviceKey: string,
    requestValue: any = null,
    options: PlanUpgradeOptions = {},
  ) => {
    const checkPlanResult = checkUpgradeNeeded(
      user,
      serviceKey,
      requestValue,
    );

    return showUpgrade(checkPlanResult, options);
  }, [showUpgrade, user]);

  const value: PlanUpgradeContextValue = useMemo(
    () => ({ showUpgrade, clearUpgrade, checkAndShow }),
    [showUpgrade, clearUpgrade, checkAndShow],
  );

  const handleClose = useCallback(() => {
    const onClose = upgradeState?.options?.onClose;
    clearUpgrade();
    onClose?.();
  }, [clearUpgrade, upgradeState?.options?.onClose]);

  return (
    <PlanUpgradeContext.Provider value={value}>
      {children}
      {upgradeState && (
        <CheckPlanUpgrade
          user={user}
          event={upgradeState.options.event}
          description={upgradeState.options.description}
          checkPlanResult={upgradeState.checkPlanResult}
          onClose={handleClose}
        />
      )}
    </PlanUpgradeContext.Provider>
  );
}

export function usePlanUpgrade(): PlanUpgradeContextValue {
  const context = useContext(PlanUpgradeContext);

  if (!context) {
    throw new Error('usePlanUpgrade must be used within PlanUpgradeProvider');
  }

  return context;
}

